/* ============================================================
   VITALCASA CRM — Pruebas Scraping
   Vista Pipeline + Vista Tabla. Usa theme.colors del CRM.
   ============================================================ */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../components/ThemeProvider';
import api from '../services/api';
import { dialogs } from '../context/DialogContext';
import usePermiso from '../hooks/usePermiso';

const IMG_PROXY_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:4000/api/img-proxy?url='
  : 'https://vitalcasa-crm-production.up.railway.app/api/img-proxy?url=';

const proxyFoto = (url) => url ? IMG_PROXY_BASE + encodeURIComponent(url) : url;

const EXTRAS_ES = {
  air_conditioner: 'Aire acondicionado', heating: 'Calefacción', furnished: 'Amueblado',
  balcony: 'Balcón', terrace: 'Terraza', garage: 'Garaje', parking: 'Parking',
  storage: 'Trastero', pool: 'Piscina', garden: 'Jardín', elevator: 'Ascensor',
  equiped_kitchen: 'Cocina equipada', antiquity: 'Antigüedad', bathrooms: 'Baños',
  conservationStatus: 'Estado conservación', floor: 'Planta', orientation: 'Orientación',
  rooms: 'Habitaciones', surface: 'Superficie', new_construction: 'Obra nueva',
  second_hand: 'Segunda mano', good_condition: 'Buen estado', need_renovation: 'A reformar',
  luxury: 'Lujo', duplex: 'Dúplex', penthouse: 'Ático', studio: 'Estudio',
  ground_floor: 'Planta baja', alarm: 'Alarma', video_intercom: 'Videoportero',
  laundry: 'Lavandería', pets_allowed: 'Se admiten mascotas', adapted: 'Adaptado minusválidos',
};
const traducirExtra = (ex) => EXTRAS_ES[ex] || ex.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
import ModalWhatsApp from '../components/ModalWhatsApp';
import ModalEmail    from '../components/ModalEmail';

// ── Traducción tipos de propiedad (valores inglés legacy → español) ────────────
const TIPO_ES = {
  flat:'Piso', apartment:'Apartamento', studio:'Estudio', loft:'Loft',
  duplex:'Dúplex', penthouse:'Ático', ground_floor:'Planta baja', mezzanine:'Entresuelo',
  house:'Casa', chalet:'Chalet', detached_house:'Chalet independiente',
  semi_detached:'Adosado', terraced_house:'Adosado', bungalow:'Bungalow',
  village_house:'Casa de pueblo', country_house:'Casa rural',
  farmhouse:'Finca rústica', rural:'Finca rústica', country:'Finca rústica', cortijo:'Cortijo',
  building:'Edificio', land:'Terreno', garage:'Garaje', storage_room:'Trastero',
  office:'Oficina', premises:'Local comercial', new_construction:'Obra nueva',
};
const traducirTipo = t => {
  if (!t) return '—';
  const key = t.toLowerCase().replace(/-/g,'_');
  return TIPO_ES[key] || t.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtFecha = iso => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' });
};
const fmtPrecio = n => n ? new Intl.NumberFormat('es-ES').format(n) + ' €' : null;

// ── Helpers ───────────────────────────────────────────────────────────────────
// Un inmueble es "probable particular" si Fotocasa lo marca como Particular
// o si tiene dirección completa (calle + número) — los particulares no ocultan el portal.
function esProbableParticular(item) {
  return ['particular','private'].includes((item.tipo_anunciante || '').toLowerCase())
    || item.caracteristicas?.tiene_num_calle === true;
}

// ── Badge tipo anunciante ─────────────────────────────────────────────────────
function Badge({ tipo, agencia, item, t }) {
  const esParticular = esProbableParticular(item || { tipo_anunciante: tipo });
  const cfg = esParticular
    ? { bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' }
    : {
        Agencia:  { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6' },
        Promotor: { bg: 'rgba(139,92,246,0.15)', color: '#8b5cf6' },
      }[tipo] || { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8' };

  const label = esParticular
    ? (tipo === 'Particular' ? 'Particular' : '📍 Probable Particular')
    : (tipo === 'Agencia' && agencia ? agencia : (tipo || '—'));

  return (
    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700, backgroundColor: cfg.bg, color: cfg.color }}>
      {label}
    </span>
  );
}

// ── Modal Detalle ─────────────────────────────────────────────────────────────
function ModalDetalle({ item, onClose, onDescartar, t }) {
  const [fotoIdx,    setFotoIdx]    = useState(0);
  const [loaded,     setLoaded]     = useState(false);
  const [modalWa,    setModalWa]    = useState(false);
  const [modalEmail, setModalEmail] = useState(false);
  const fotos = item.fotos || [];

  // Resetear índice y estado al cambiar de propiedad
  useEffect(() => { setFotoIdx(0); setLoaded(false); }, [item.id]);

  // Precargar todas las fotos en background al abrir el modal
  // El navegador las cachea → navegación instantánea
  useEffect(() => {
    if (fotos.length === 0) return;
    fotos.forEach(url => {
      const img = new Image();
      img.src = proxyFoto(url);
    });
  }, [item.id]);
  const car   = item.caracteristicas || {};
  const tel   = item.telefonos?.[0] || '';
  const email = item.emails?.[0]    || '';
  const nombre = item.nombre_contacto || '';
  const leadSimulado = {
    id: item.id,
    nombre:   nombre.split(' ')[0] || '',
    apellido: nombre.split(' ').slice(1).join(' ') || '',
    telefono: tel, email,
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:16, backgroundColor:'rgba(0,0,0,0.65)', backdropFilter:'blur(4px)' }}
      onClick={onClose}>
      <div style={{ position:'relative', width:'100%', maxWidth:1000, maxHeight:'92vh', borderRadius:16, backgroundColor:t.bgCard, color:t.textPrimary, boxShadow:'0 24px 60px rgba(0,0,0,0.5)', display:'flex', flexDirection:'column', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Cabecera */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', borderBottom:`1px solid ${t.border}`, flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:20 }}>🏠</span>
            <div>
              <h2 style={{ fontSize:15, fontWeight:700, margin:0 }}>{item.direccion || 'Sin dirección'}</h2>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:3 }}>
                <Badge tipo={item.tipo_anunciante} agencia={item.alias_agencia} item={item} t={t} />
                {item.portal && (
                  <span style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:600, backgroundColor:'rgba(249,115,22,0.15)', color:'#f97316', textTransform:'capitalize' }}>
                    {item.portal}
                  </span>
                )}
                {item.fecha_publicacion && (
                  <span style={{ fontSize:11, color:t.textMuted }}>Publicado: {fmtFecha(item.fecha_publicacion)}</span>
                )}
                <span style={{ fontSize:11, color:t.textMuted }}>Importado: {fmtFecha(item.created_at)}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ padding:'6px 10px', borderRadius:8, border:`1px solid ${t.border}`, backgroundColor:'transparent', color:t.textMuted, cursor:'pointer', fontSize:14 }}>✕</button>
        </div>

        {/* Cuerpo 2 columnas */}
        <div style={{ flex:1, overflow:'hidden', display:'grid', gridTemplateColumns:'1fr 1fr' }}>

          {/* Col izquierda: fotos + descripción — scroll independiente */}
          <div style={{ padding:20, borderRight:`1px solid ${t.border}`, display:'flex', flexDirection:'column', gap:14, overflowY:'auto' }}>
            {fotos.length > 0 ? (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {/* Foto principal: altura fija, contain para no recortar verticales */}
                <div style={{ position:'relative', width:'100%', height:280, borderRadius:10, overflow:'hidden', backgroundColor:'#111', flexShrink:0 }}>
                  <img
                    key={fotoIdx}
                    src={proxyFoto(fotos[fotoIdx])}
                    alt=""
                    onLoad={() => setLoaded(true)}
                    onError={e => { e.target.src='https://via.placeholder.com/800x450?text=Sin+imagen'; setLoaded(true); }}
                    style={{
                      width:'100%', height:'100%', objectFit:'contain',
                      opacity: loaded ? 1 : 0,
                      transition: 'opacity 0.15s ease',
                    }}
                  />
                  {/* Spinner mientras carga la foto actual */}
                  {!loaded && (
                    <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <div style={{ width:24, height:24, border:'3px solid rgba(255,255,255,0.2)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
                    </div>
                  )}
                  {fotos.length > 1 && (
                    <>
                      <button onClick={() => { setLoaded(false); setFotoIdx(i => (i - 1 + fotos.length) % fotos.length); }}
                        style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', padding:'6px 10px', backgroundColor:'rgba(0,0,0,0.55)', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:18 }}>‹</button>
                      <button onClick={() => { setLoaded(false); setFotoIdx(i => (i + 1) % fotos.length); }}
                        style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', padding:'6px 10px', backgroundColor:'rgba(0,0,0,0.55)', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:18 }}>›</button>
                      <div style={{ position:'absolute', bottom:8, left:'50%', transform:'translateX(-50%)', backgroundColor:'rgba(0,0,0,0.55)', color:'#fff', fontSize:11, padding:'2px 8px', borderRadius:20 }}>
                        {fotoIdx + 1} / {fotos.length}
                      </div>
                    </>
                  )}
                </div>
                {/* Thumbnails: franja fija, scroll horizontal */}
                {fotos.length > 1 && (
                  <div style={{ display:'flex', gap:4, overflowX:'auto', height:50, flexShrink:0, paddingBottom:2 }}>
                    {fotos.map((f, i) => (
                      <button key={i} onClick={() => setFotoIdx(i)}
                        style={{ flexShrink:0, width:72, height:48, borderRadius:5, overflow:'hidden', border:`2px solid ${i === fotoIdx ? t.accentBlue : 'transparent'}`, opacity: i === fotoIdx ? 1 : 0.55, cursor:'pointer', padding:0, backgroundColor:'#111' }}>
                        <img src={f} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}
                          onError={e => { e.target.style.display='none'; }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ height:280, borderRadius:10, backgroundColor:t.bgPanel, display:'flex', alignItems:'center', justifyContent:'center', color:t.textMuted, fontSize:13, flexShrink:0 }}>Sin fotos</div>
            )}

            {/* Descripción */}
            <div>
              <p style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Descripción</p>
              <p style={{ fontSize:12, lineHeight:1.6, color:t.textSecondary }}>{item.descripcion || 'Sin descripción disponible.'}</p>
            </div>
          </div>

          {/* Col derecha: datos */}
          <div style={{ padding:20, display:'flex', flexDirection:'column', gap:14, overflowY:'auto' }}>

            {/* Precio */}
            {item.precio_numerico && (
              <div style={{ fontSize:26, fontWeight:800, color:t.accentBlue }}>{fmtPrecio(item.precio_numerico)}</div>
            )}

            {/* Badge eliminado */}
            {(item.estado || 'activo') === 'eliminado' && (
              <div style={{ backgroundColor:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:8, padding:'8px 12px' }}>
                <span style={{ fontSize:12, fontWeight:700, color:'#ef4444' }}>❌ Inmueble retirado del portal</span>
                {item.motivo_eliminacion && <p style={{ fontSize:11, color:'#ef4444', margin:'4px 0 0', opacity:0.8 }}>{item.motivo_eliminacion}</p>}
              </div>
            )}

            {/* Contacto */}
            <div style={{ backgroundColor:t.bgPanel, borderRadius:10, padding:14 }}>
              <p style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>Contacto</p>
              {[
                [item.tipo_anunciante === 'Particular' ? 'Propietario' : 'Agencia', item.nombre_contacto || item.alias_agencia || '—', null],
                ['Teléfono',    item.telefonos?.[0] || '—',  item.telefonos?.[0] ? `tel:${item.telefonos[0]}` : null],
                ['Email',       item.emails?.[0]    || '—',  item.emails?.[0]    ? `mailto:${item.emails[0]}` : null],
                ['Referencia',  item.referencia     || '—',  null],
              ].map(([k, v, href]) => (
                <div key={k} style={{ display:'grid', gridTemplateColumns:'90px 1fr', gap:6, marginBottom:5 }}>
                  <span style={{ fontSize:11, color:t.textMuted }}>{k}</span>
                  {href
                    ? <a href={href} style={{ fontSize:11, fontWeight:600, color:t.accentBlue, wordBreak:'break-all', textDecoration:'none' }}>{v}</a>
                    : <span style={{ fontSize:11, fontWeight:600, color:t.textPrimary, wordBreak:'break-all' }}>{v}</span>
                  }
                </div>
              ))}
            </div>

            {/* Propiedad */}
            <div style={{ backgroundColor:t.bgPanel, borderRadius:10, padding:14 }}>
              <p style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>Propiedad</p>
              {[
                ['Operación',   car.operacion || null],
                ['Tipo',        traducirTipo(car.tipo_propiedad || item.tipo)],
                ['Habitaciones',car.habitaciones != null ? car.habitaciones : null],
                ['Baños',       car.banos        != null ? car.banos        : null],
                ['Superficie',  car.superficie_m2      ? `${car.superficie_m2} m²`      : null],
                ['Sup. útil',   car.superficie_util    ? `${car.superficie_util} m²`    : null],
                ['Planta',      car.planta ?? null],
                ['Orientación', car.orientacion ?? null],
                ['Tipo suelo',  car.tipo_suelo ?? null],
                ['Parcela',     car.superficie_parcela_m2 ? `${car.superficie_parcela_m2} m²` : null],
                ['Energía',     car.certificacion_energetica || car.certificado_energia || null],
              ].filter(([, v]) => v !== null && v !== undefined)
               .map(([k, v]) => (
                <div key={k} style={{ display:'grid', gridTemplateColumns:'90px 1fr', gap:6, marginBottom:5 }}>
                  <span style={{ fontSize:11, color:t.textMuted }}>{k}</span>
                  <span style={{ fontSize:11, color:t.textPrimary }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Características */}
            {(() => {
              const chips = [
                car.aire_acondicionado  && 'Aire acondicionado',
                car.calefaccion         && (car.tipo_calefaccion ? `Calef. ${car.tipo_calefaccion}` : 'Calefacción'),
                car.piscina             && 'Piscina',
                car.garaje              && 'Garaje',
                car.terraza             && 'Terraza',
                car.jardin              && 'Jardín',
                car.ascensor            && 'Ascensor',
                car.trastero            && 'Trastero',
                car.amueblado           && 'Amueblado',
                car.primera_linea       && 'Primera línea',
                car.vistas_mar          && 'Vistas al mar',
                car.agua                && 'Agua',
                car.gas                 && 'Gas',
                car.luz                 && 'Luz',
                car.telefono_vivienda   && 'Teléfono',
                car.interior            && 'Interior',
                car.exterior            && 'Exterior',
                ...(car.todos_extras || []).map(ex => traducirExtra(ex)),
              ].filter(Boolean);
              if (chips.length === 0) return null;
              return (
                <div style={{ backgroundColor:t.bgPanel, borderRadius:10, padding:14 }}>
                  <p style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>Características</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                    {chips.map((chip, i) => (
                      <span key={i} style={{ padding:'3px 8px', borderRadius:6, fontSize:11, backgroundColor:t.chipBg, color:t.chipText, border:`1px solid ${t.chipBorder}` }}>{chip}</span>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Localización */}
            <div>
              <p style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Localización</p>
              <p style={{ fontSize:12, color:t.textSecondary }}>{[item.direccion, item.municipio, item.provincia].filter(Boolean).join(', ') || '—'}</p>
              {item.codigo_postal && <p style={{ fontSize:11, color:t.textMuted }}>CP: {item.codigo_postal}</p>}
            </div>

            {/* Metadatos */}
            <div style={{ fontSize:11, color:t.textMuted, paddingTop:10, borderTop:`1px solid ${t.borderLight}` }}>
              <div>Publicado: {item.fecha_publicacion ? fmtFecha(item.fecha_publicacion) : '—'}</div>
              <div>Entrada CRM: {fmtFecha(item.created_at)}</div>
              {item.url_original && (
                <a href={item.url_original} target="_blank" rel="noopener noreferrer"
                  style={{ color:t.accentBlue, textDecoration:'none' }}>
                  Ver en {item.portal || 'portal'} ↗
                </a>
              )}
            </div>

            {/* Acciones */}
            <div style={{ display:'flex', gap:8, marginTop:'auto' }}>
              {tel && (
                <>
                  <a href={`tel:${tel}`} style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px 0', background:t.btnCallBg, color:'#fff', borderRadius:10, fontSize:12, fontWeight:600, textDecoration:'none' }}>
                    📞 Llamar
                  </a>
                  <button onClick={() => setModalWa(true)}
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px 0', background:t.btnWhatsappBg, color:'#fff', border:'none', borderRadius:10, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    💬 WhatsApp
                  </button>
                </>
              )}
              {email && (
                <button onClick={() => setModalEmail(true)}
                  style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px 0', backgroundColor:t.accentBlue, color:'#fff', border:'none', borderRadius:10, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  ✉️ Email
                </button>
              )}
              <button onClick={() => { onDescartar(item.id); onClose(); }}
                style={{ padding:'9px 14px', backgroundColor:t.bgPanel, color:t.textMuted, border:`1px solid ${t.border}`, borderRadius:10, fontSize:12, cursor:'pointer' }}>
                🗑 Descartar
              </button>
            </div>
          </div>
        </div>
      </div>

      {modalWa    && <ModalWhatsApp lead={leadSimulado} onClose={() => setModalWa(false)} />}
      {modalEmail && <ModalEmail    lead={leadSimulado} onClose={() => setModalEmail(false)} />}
    </div>
  );
}

// ── Tarjeta Pipeline ──────────────────────────────────────────────────────────
function TarjetaScraping({ item, onClick, t }) {
  const car  = item.caracteristicas || {};
  const foto = item.fotos?.[0];

  return (
    <div onClick={onClick} style={{
      backgroundColor: t.bgCard, border:`1px solid ${t.border}`, borderRadius:12,
      overflow:'hidden', cursor:'pointer', transition:'all 0.18s',
      boxShadow: t.propCardShadow,
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = t.accentBlue; e.currentTarget.style.boxShadow = t.propCardHoverShadow; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = t.border;     e.currentTarget.style.boxShadow = t.propCardShadow; }}
    >
      {/* Foto 16:9 */}
      <div style={{ position:'relative', width:'100%', aspectRatio:'16/9', overflow:'hidden', backgroundColor:t.bgPanel }}>
        {foto
          ? <img src={foto} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}
              onError={e => { e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:11px">Sin foto</div>`; }} />
          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24 }}>🏠</div>
        }
        {/* Badges encima de la foto */}
        <div style={{ position:'absolute', top:6, left:6, display:'flex', gap:4 }}>
          <Badge tipo={item.tipo_anunciante} agencia={item.alias_agencia} item={item} t={t} />
          {(item.estado || 'activo') === 'eliminado' && (
            <span style={{ padding:'2px 6px', borderRadius:20, fontSize:9, fontWeight:700, backgroundColor:'rgba(239,68,68,0.85)', color:'#fff' }}>RETIRADO</span>
          )}
        </div>
        {item.total_fotos > 1 && (
          <div style={{ position:'absolute', bottom:6, right:6, backgroundColor:'rgba(0,0,0,0.6)', color:'#fff', fontSize:10, padding:'2px 6px', borderRadius:20 }}>
            📸 {item.total_fotos}
          </div>
        )}
      </div>

      {/* Cuerpo */}
      <div style={{ padding:'10px 12px', display:'flex', flexDirection:'column', gap:5 }}>
        <p style={{ fontSize:12, fontWeight:700, color:t.textPrimary, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {item.direccion || item.titulo || 'Sin dirección'}
        </p>
        <div style={{ fontSize:11, color:t.textMuted, display:'flex', gap:4 }}>
          {car.tipo_propiedad && <span>{traducirTipo(car.tipo_propiedad)}</span>}
          {car.habitaciones != null && <span>· {car.habitaciones} dorm.</span>}
          {car.banos        != null && <span>· {car.banos} baños</span>}
          {car.superficie_m2 != null && <span>· {car.superficie_m2} m²</span>}
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:11, color:t.textMuted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'60%' }}>
            {item.nombre_contacto || '—'}
          </span>
          {item.telefonos?.[0] && (
            <span style={{ fontSize:11, fontFamily:'monospace', fontWeight:700, color:t.accentBlue }}>{item.telefonos[0]}</span>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:10, color:t.textMuted, textTransform:'capitalize' }}>{item.portal || 'Portal'}</span>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', lineHeight:1.15 }}>
            <span style={{ fontSize:9, color:t.textMuted }} title="Cuándo se guardó en el CRM (importación / alta en scraping)">
              CRM {fmtFecha(item.created_at)}
            </span>
            {item.fecha_publicacion && (
              <span style={{ fontSize:9, color:t.textMuted, opacity:0.85 }} title="Fecha de publicación que muestra el portal (puede ser más reciente que el alta en CRM)">
                Port. {fmtFecha(item.fecha_publicacion)}
              </span>
            )}
          </div>
        </div>
        {item.precio_numerico && (
          <p style={{ fontSize:13, fontWeight:800, color:t.accentBlue, margin:0 }}>{fmtPrecio(item.precio_numerico)}</p>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function PruebasScraping() {
  const { theme } = useTheme();
  const t = theme.colors;
  const { puede } = usePermiso();
  const verLanzar  = puede('scr_lanzar', 'ver');
  const verPruebas = puede('scr_pruebas', 'ver');

  const [busquedas,        setBusquedas]        = useState([]);
  const [totalPorBusqueda, setTotalPorBusqueda] = useState({});
  const [resultados,       setResultados]        = useState([]);
  const [totalResultados,  setTotalResultados]   = useState(0);
  const [offsetResultados, setOffsetResultados]  = useState(0);
  const [cargandoMas,      setCargandoMas]       = useState(false);
  const [cargando,         setCargando]          = useState(true);
  const PAGE_SIZE = 200;
  const [vista,            setVista]             = useState('pipeline');
  const [filtroAnunciante, setFiltroAnunciante]  = useState('todos');
  const [filtroEstado,     setFiltroEstado]      = useState('activo');
  const [detalleItem,      setDetalleItem]       = useState(null);
  const [seleccionados,    setSeleccionados]     = useState(new Set());
  const [ejecutando,       setEjecutando]        = useState({});
  const [jobEstados,       setJobEstados]        = useState({});
  const [testeando,        setTesteando]         = useState({});
  const [testResultados,   setTestResultados]    = useState({});
  const [modalWaItem,      setModalWaItem]       = useState(null);
  const [modalEmailItem,   setModalEmailItem]    = useState(null);
  const [enriqueciendo,    setEnriqueciendo]     = useState(false);
  const [enriqEstado,      setEnriqEstado]       = useState(null);
  const [enrichPortalSt,   setEnrichPortalSt]    = useState({ idealista: null, pisos: null, habitaclia: null });
  const enrichPortalPollRef = useRef(null);
  const [biseccionEstados, setBiseccionEstados]  = useState({});
  const [idealistaEstados, setIdealistaEstados]  = useState({});
  const [showNuevaIdealista, setShowNuevaIdealista] = useState(false);
  const [nuevaIdealistaNombre, setNuevaIdealistaNombre] = useState('');
  const [nuevaIdealistaUrl,    setNuevaIdealistaUrl]    = useState('https://www.idealista.com/venta-viviendas/');
  const idealistaPollRefs = useRef({});
  const [pisosEstados,      setPisosEstados]      = useState({});
  const pisosPollRefs = useRef({});
  const [habitacliaEstados, setHabitacliaEstados] = useState({});
  const habitacliaPollRefs = useRef({});
  const [filtroTipo,       setFiltroTipo]        = useState('todos');
  const [filtroDorm,       setFiltroDorm]        = useState('todos');
  const [filtroPrecioMin,  setFiltroPrecioMin]   = useState('');
  const [filtroPrecioMax,  setFiltroPrecioMax]   = useState('');
  const [ordenacion,       setOrdenacion]        = useState('fecha_desc');
  const pollingRefs = useRef({});
  const enriqPollRef = useRef(null);
  const biseccionPollRefs = useRef({});

  // ── Polling para enriquecimiento de portales ────────────────────────────────
  const iniciarPollEnriquecimiento = useCallback(() => {
    if (enrichPortalPollRef.current) return;
    enrichPortalPollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get('/enrich/status');
        setEnrichPortalSt(data);
        const anyRunning = Object.values(data).some(s => s?.estado === 'running');
        if (!anyRunning) {
          clearInterval(enrichPortalPollRef.current);
          enrichPortalPollRef.current = null;
        }
      } catch { /* ignorar errores de polling */ }
    }, 3000);
  }, []);

  useEffect(() => {
    return () => { if (enrichPortalPollRef.current) clearInterval(enrichPortalPollRef.current); };
  }, []);

  const lanzarEnriquecimientoPortal = useCallback(async (portal, endpoint) => {
    try {
      await api.post(endpoint);
      setEnrichPortalSt(prev => ({ ...prev, [portal]: { estado: 'running', total: '?', procesados: 0, conDatos: 0 } }));
      iniciarPollEnriquecimiento();
    } catch (e) { await dialogs.alert('Error al iniciar enriquecimiento: ' + e.message); }
  }, [iniciarPollEnriquecimiento]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const resBusc = await api.get('/scraping/busquedas');
      const busc = resBusc.data;
      setBusquedas(busc);

      // Cargar resultados por búsqueda para garantizar que todas aparecen,
      // independientemente del orden de created_at
      const POR_BUSQUEDA = 500;
      const llamadas = [
        api.get(`/scraping/resultados?limit=${PAGE_SIZE}&offset=0`),
        ...busc.map(b => api.get(`/scraping/resultados?busqueda_id=${b.id}&limit=${POR_BUSQUEDA}&offset=0`)),
      ];
      const settled = await Promise.allSettled(llamadas);

      // Capturar total real por búsqueda (la API devuelve { resultados, total })
      const totales = {};
      for (let i = 0; i < busc.length; i++) {
        const s = settled[i + 1]; // +1 porque el índice 0 es la llamada global
        if (s?.status === 'fulfilled') {
          totales[busc[i].id] = s.value.data?.total ?? 0;
        }
      }
      setTotalPorBusqueda(totales);

      // Combinar y deduplicar por id (ignorar llamadas fallidas)
      const mapa = new Map();
      for (const s of settled) {
        if (s.status !== 'fulfilled') continue;
        const items = s.value.data.resultados ?? s.value.data;
        if (!Array.isArray(items)) continue;
        for (const it of items) mapa.set(it.id, it);
      }
      const todos = Array.from(mapa.values());
      const firstOk = settled.find(s => s.status === 'fulfilled');
      const total = firstOk?.value?.data?.total ?? todos.length;

      setResultados(todos);
      setTotalResultados(total);
      setOffsetResultados(PAGE_SIZE);
    } catch (e) {
      console.error('[PruebasScraping]', e.message);
    } finally { setCargando(false); }
  }, []);

  const cargarMas = async () => {
    if (cargandoMas) return;
    setCargandoMas(true);
    try {
      const res = await api.get(`/scraping/resultados?limit=${PAGE_SIZE}&offset=${offsetResultados}`);
      const nuevos = res.data.resultados ?? res.data;
      setResultados(r => [...r, ...nuevos]);
      setTotalResultados(res.data.total ?? totalResultados);
      setOffsetResultados(o => o + PAGE_SIZE);
    } catch (e) {
      console.error('[PruebasScraping] cargarMas', e.message);
    } finally { setCargandoMas(false); }
  };

  const nParticulares = resultados.filter(r =>
    ['particular','private'].includes((r.tipo_anunciante || '').toLowerCase())
  ).length;

  const iniciarEnriquecimientoParticulares = async () => {
    if (nParticulares === 0) { await dialogs.alert('No hay particulares en la BD todavía.'); return; }
    if (!(await dialogs.confirm(
      `¿Enriquecer solo los ${nParticulares} particulares?\n\n` +
      `Visitará la página de detalle de cada uno para obtener dirección exacta y teléfono.\n\n` +
      `Coste estimado: ~${nParticulares * 10} créditos ScraperAPI (~${Math.ceil(nParticulares * 1.2 / 60)} min)`
    ))) return;
    try {
      setEnriqueciendo(true);
      await api.post('/scraping/enriquecer', { limite: nParticulares + 50, soloParticulares: true });
      enriqPollRef.current = setInterval(async () => {
        const r = await api.get('/scraping/enriquecer/estado');
        setEnriqEstado(r.data);
        if (!r.data.activo) {
          clearInterval(enriqPollRef.current);
          setEnriqueciendo(false);
          cargar();
        }
      }, 3000);
    } catch (e) {
      setEnriqueciendo(false);
      await dialogs.alert('Error: ' + e.message);
    }
  };

  const iniciarEnriquecimiento = async () => {
    if (!(await dialogs.confirm(`¿Iniciar enriquecimiento de las ${totalResultados} propiedades existentes?\n\nEl proceso visitará la página de detalle de cada una para obtener la dirección completa y detectar particulares.\n\nCoste estimado: ${totalResultados * 10} créditos ScraperAPI (~${Math.round(totalResultados * 1.2 / 60)} horas)`))) return;
    try {
      setEnriqueciendo(true);
      await api.post('/scraping/enriquecer', { limite: 2000 });
      enriqPollRef.current = setInterval(async () => {
        const r = await api.get('/scraping/enriquecer/estado');
        setEnriqEstado(r.data);
        if (!r.data.activo) {
          clearInterval(enriqPollRef.current);
          setEnriqueciendo(false);
          cargar();
        }
      }, 3000);
    } catch (e) {
      setEnriqueciendo(false);
      await dialogs.alert('Error: ' + e.message);
    }
  };

  const cancelarEnriquecimiento = async () => {
    await api.post('/scraping/enriquecer/cancelar');
    clearInterval(enriqPollRef.current);
    setEnriqueciendo(false);
  };

  useEffect(() => { cargar(); }, [cargar]);

  // Limpiar polling al desmontar
  useEffect(() => {
    return () => {
      Object.values(pollingRefs.current).forEach(clearInterval);
      Object.values(biseccionPollRefs.current).forEach(clearInterval);
      if (enriqPollRef.current) clearInterval(enriqPollRef.current);
    };
  }, []);

  const descartar = async id => {
    try { await api.patch(`/scraping/resultados/${id}/descartar`); setResultados(r => r.filter(x => x.id !== id)); }
    catch {}
  };

  const iniciarImportMasivo = async (busquedaId, nombre) => {
    const credEstimados = 3000;
    if (!(await dialogs.confirm(`Import masivo por bisección para "${nombre}"\n\n` +
      `Esto importará TODAS las propiedades del catálogo de Fotocasa para esta búsqueda (~3.000 props en Denia).\n\n` +
      `Coste estimado: ~${credEstimados} créditos ScraperAPI (~${Math.round(credEstimados * 0.15)} minutos).\n\n` +
      `El proceso corre en background. ¿Continuar?`))) return;

    try {
      await api.post(`/import/biseccion?busqueda_id=${busquedaId}`, { busqueda_id: busquedaId });
      setBiseccionEstados(s => ({ ...s, [busquedaId]: { estado: 'running', msg: 'Iniciando…', itemsSaved: 0, requestsMade: 0 } }));

      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/import/biseccion/status?busqueda_id=${busquedaId}`);
          const st = r.data;
          setBiseccionEstados(s => ({ ...s, [busquedaId]: st }));
          if (st.estado === 'completed' || st.estado === 'error' || st.estado === 'cancelled') {
            clearInterval(poll);
            delete biseccionPollRefs.current[busquedaId];
            if (st.estado === 'completed') cargar();
          }
        } catch { /* ignorar errores de polling */ }
      }, 3000);
      biseccionPollRefs.current[busquedaId] = poll;
    } catch (e) {
      await dialogs.alert('Error iniciando import: ' + e.message);
    }
  };

  const cancelarImportMasivo = async (busquedaId) => {
    try {
      await api.delete(`/import/biseccion?busqueda_id=${busquedaId}`);
      clearInterval(biseccionPollRefs.current[busquedaId]);
      delete biseccionPollRefs.current[busquedaId];
      setBiseccionEstados(s => ({ ...s, [busquedaId]: { ...s[busquedaId], estado: 'cancelled', msg: 'Cancelado' } }));
    } catch (e) { await dialogs.alert('Error: ' + e.message); }
  };

  // ── Idealista ──────────────────────────────────────────────────────────────
  const crearBusquedaIdealista = async () => {
    if (!nuevaIdealistaNombre.trim()) {
      await dialogs.alert('Escribe un nombre para la búsqueda');
      return;
    }
    if (!nuevaIdealistaUrl.trim()) {
      await dialogs.alert('Escribe al menos una URL de Idealista');
      return;
    }
    // Soportar múltiples URLs separadas por salto de línea o coma
    const urls = nuevaIdealistaUrl
      .split(/[\n,]+/)
      .map(u => u.trim())
      .filter(u => u.startsWith('http'));
    if (!urls.length) {
      await dialogs.alert('Ninguna URL válida (deben empezar por http)');
      return;
    }
    try {
      await api.post('/scraping/busquedas', {
        nombre:      nuevaIdealistaNombre.trim(),
        portal:      'idealista',
        operacion:   'comprar',
        tipo:        null,
        poblaciones: urls.map(u => new URL(u).pathname.split('/').filter(Boolean)[1] || u),
        extras:      { url_idealista: urls[0], urls_idealista: urls },
      });
      setShowNuevaIdealista(false);
      setNuevaIdealistaNombre('');
      setNuevaIdealistaUrl('https://www.idealista.com/venta-viviendas/');
      await cargar();
    } catch (e) { await dialogs.alert('Error creando búsqueda: ' + e.message); }
  };

  const iniciarImportIdealista = async (busquedaId, col, nombre) => {
    const urls = col?.extras?.urls_idealista || (col?.extras?.url_idealista ? [col.extras.url_idealista] : []);
    if (!urls.length) {
      await dialogs.alert('Esta búsqueda no tiene URL de Idealista configurada');
      return;
    }
    const est = idealistaEstados[busquedaId];
    if (est?.estado === 'scraping') {
      await dialogs.alert('Import ya en curso');
      return;
    }
    const resumen = urls.length === 1 ? urls[0] : `${urls.length} URLs (${urls.map(u => u.split('/').filter(Boolean).pop()).join(', ')})`;
    if (!(await dialogs.confirm(`Import masivo Idealista para "${nombre}"\n\n${resumen}\n\nDescargará hasta 80 páginas × 30 = 2.400 anuncios por URL.\nCoste: ~${urls.length * 80} créditos ultra_premium de ScraperAPI.\n\n¿Continuar?`))) return;
    try {
      // Lanzar import para la primera URL (las demás se hacen en secuencia en el service)
      await api.post('/import/idealista', { busqueda_id: busquedaId, url_base: urls[0], urls_base: urls, max_paginas: 80 });
      setIdealistaEstados(s => ({ ...s, [busquedaId]: { estado: 'scraping', pagina: 0, guardados: 0, duplicados: 0 } }));

      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/import/idealista/status?busqueda_id=${busquedaId}`);
          const st = r.data;
          setIdealistaEstados(s => ({ ...s, [busquedaId]: st }));
          if (st.estado === 'completado' || st.estado === 'error' || st.estado === 'cancelado') {
            clearInterval(poll);
            delete idealistaPollRefs.current[busquedaId];
            if (st.estado === 'completado') cargar();
          }
        } catch { /* ignorar errores de polling */ }
      }, 4000);
      idealistaPollRefs.current[busquedaId] = poll;
    } catch (e) { await dialogs.alert('Error iniciando import: ' + e.message); }
  };

  const cancelarImportIdealista = async (busquedaId) => {
    try {
      await api.delete(`/import/idealista?busqueda_id=${busquedaId}`);
      clearInterval(idealistaPollRefs.current[busquedaId]);
      delete idealistaPollRefs.current[busquedaId];
      setIdealistaEstados(s => ({ ...s, [busquedaId]: { ...s[busquedaId], estado: 'cancelado' } }));
    } catch (e) { await dialogs.alert('Error: ' + e.message); }
  };

  const iniciarImportPisos = async (busquedaId, col, nombre) => {
    const urls = col?.extras?.urls_pisos || (col?.extras?.url_pisos ? [col.extras.url_pisos] : []);
    if (!urls.length) {
      await dialogs.alert('Esta búsqueda no tiene URL de Pisos.com configurada');
      return;
    }
    const est = pisosEstados[busquedaId];
    if (est?.estado === 'scraping') {
      await dialogs.alert('Import ya en curso');
      return;
    }
    const resumen = urls.length === 1 ? urls[0] : `${urls.length} URLs (${urls.map(u => new URL(u).pathname.split('/').filter(Boolean).pop()).join(', ')})`;
    if (!(await dialogs.confirm(`Import masivo Pisos.com para "${nombre}"\n\n${resumen}\n\nDescargará hasta 50 páginas por URL.\nCoste: ~${urls.length * 50} créditos premium de ScraperAPI.\n\n¿Continuar?`))) return;
    try {
      await api.post('/import/pisos', { busqueda_id: busquedaId, url_base: urls[0], urls_base: urls, max_paginas: 50 });
      setPisosEstados(s => ({ ...s, [busquedaId]: { estado: 'scraping', pagina: 0, guardados: 0, duplicados: 0 } }));

      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/import/pisos/status?busqueda_id=${busquedaId}`);
          const st = r.data;
          setPisosEstados(s => ({ ...s, [busquedaId]: st }));
          if (st.estado === 'completado' || st.estado === 'error' || st.estado === 'cancelado') {
            clearInterval(poll);
            delete pisosPollRefs.current[busquedaId];
            if (st.estado === 'completado') cargar();
          }
        } catch { /* ignorar */ }
      }, 4000);
      pisosPollRefs.current[busquedaId] = poll;
    } catch (e) { await dialogs.alert('Error iniciando import: ' + e.message); }
  };

  const cancelarImportPisos = async (busquedaId) => {
    try {
      await api.delete(`/import/pisos?busqueda_id=${busquedaId}`);
      clearInterval(pisosPollRefs.current[busquedaId]);
      delete pisosPollRefs.current[busquedaId];
      setPisosEstados(s => ({ ...s, [busquedaId]: { ...s[busquedaId], estado: 'cancelado' } }));
    } catch (e) { await dialogs.alert('Error: ' + e.message); }
  };

  const iniciarImportHabitaclia = async (busquedaId, col, nombre) => {
    const urls = col?.extras?.urls_habitaclia || (col?.extras?.url_habitaclia ? [col.extras.url_habitaclia] : []);
    if (!urls.length) {
      await dialogs.alert('Esta búsqueda no tiene URL de Habitaclia configurada');
      return;
    }
    const est = habitacliaEstados[busquedaId];
    if (est?.estado === 'scraping') {
      await dialogs.alert('Import ya en curso');
      return;
    }
    const resumen = urls.length === 1 ? urls[0] : `${urls.length} URLs`;
    if (!(await dialogs.confirm(`Import masivo Habitaclia para "${nombre}"\n\n${resumen}\n\nDescargará hasta 200 páginas (~16 anuncios/pág = 3.200 anuncios por URL).\nCoste: ~${urls.length * 200} créditos premium ScraperAPI.\n\n¿Continuar?`))) return;
    try {
      await api.post('/import/habitaclia', { busqueda_id: busquedaId, url_base: urls[0], urls_base: urls, max_paginas: 200 });
      setHabitacliaEstados(s => ({ ...s, [busquedaId]: { estado: 'scraping', pagina: 0, guardados: 0 } }));
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/import/habitaclia/status?busqueda_id=${busquedaId}`);
          const st = r.data;
          setHabitacliaEstados(s => ({ ...s, [busquedaId]: st }));
          if (['completado', 'error', 'cancelado'].includes(st.estado)) {
            clearInterval(poll);
            delete habitacliaPollRefs.current[busquedaId];
            if (st.estado === 'completado') cargar();
          }
        } catch { /* ignorar */ }
      }, 4000);
      habitacliaPollRefs.current[busquedaId] = poll;
    } catch (e) { await dialogs.alert('Error iniciando import: ' + e.message); }
  };

  const cancelarImportHabitaclia = async (busquedaId) => {
    try {
      await api.delete(`/import/habitaclia?busqueda_id=${busquedaId}`);
      clearInterval(habitacliaPollRefs.current[busquedaId]);
      delete habitacliaPollRefs.current[busquedaId];
      setHabitacliaEstados(s => ({ ...s, [busquedaId]: { ...s[busquedaId], estado: 'cancelado' } }));
    } catch (e) { await dialogs.alert('Error: ' + e.message); }
  };

  const ejecutarBusqueda = async id => {
    setEjecutando(e => ({ ...e, [id]: true }));
    setJobEstados(s => ({ ...s, [id]: { estado: 'iniciando', msg: 'Iniciando scraping…', total: 0, procesados: 0, nuevos: 0 } }));
    try {
      await api.post(`/scraping/ejecutar/${id}`);
      const startedAt = Date.now();
      const pollInterval = setInterval(async () => {
        try {
          const r = await api.get(`/scraping/estado/${id}`);
          const est = r.data;
          if (est?.estado && est.estado !== 'inactivo') {
            setJobEstados(s => ({ ...s, [id]: est }));
          }
          const elapsed = Date.now() - startedAt;
          if (est?.estado === 'terminado' || est?.estado === 'error') {
            clearInterval(pollInterval);
            delete pollingRefs.current[id];
            setEjecutando(e => ({ ...e, [id]: false }));
            if (est?.estado === 'terminado') await cargar();
          } else if (est?.estado === 'inactivo' && elapsed > 90000) {
            clearInterval(pollInterval);
            delete pollingRefs.current[id];
            setEjecutando(e => ({ ...e, [id]: false }));
            setJobEstados(s => ({ ...s, [id]: { estado: 'terminado', msg: 'Completado' } }));
            await cargar();
          }
        } catch { /* ignorar errores de polling */ }
      }, 1500);
      pollingRefs.current[id] = pollInterval;
    } catch (e) {
      setEjecutando(e => ({ ...e, [id]: false }));
      setJobEstados(s => ({ ...s, [id]: { estado: 'error', msg: e.message } }));
    }
  };

  // Tipos únicos presentes en los datos (para el selector dinámico)
  // Usamos el valor raw como key del filtro y traducirTipo() para mostrar la etiqueta
  const tiposDisponibles = [...new Set(
    resultados.map(r => r.caracteristicas?.tipo_propiedad).filter(Boolean)
  )].sort();

  const resFiltrados = resultados.filter(r => {
    const car = r.caracteristicas || {};

    // Anunciante
    let okAnunciante;
    if (filtroAnunciante === 'todos') {
      okAnunciante = true;
    } else if (filtroAnunciante === 'Particular') {
      okAnunciante = esProbableParticular(r);
    } else {
      okAnunciante = r.tipo_anunciante === filtroAnunciante && !esProbableParticular(r);
    }

    // Estado
    const okEstado = filtroEstado === 'todos' || (r.estado || 'activo') === filtroEstado;

    // Tipo de propiedad
    const okTipo = filtroTipo === 'todos' || (car.tipo_propiedad || '') === filtroTipo;

    // Dormitorios
    const dorm = car.habitaciones ?? null;
    const okDorm = filtroDorm === 'todos'
      || (filtroDorm === '5+' ? dorm >= 5 : dorm === parseInt(filtroDorm));

    // Precio
    const precio = r.precio ?? null;
    const okPrecioMin = filtroPrecioMin === '' || precio === null || precio >= parseInt(filtroPrecioMin);
    const okPrecioMax = filtroPrecioMax === '' || precio === null || precio <= parseInt(filtroPrecioMax);

    return okAnunciante && okEstado && okTipo && okDorm && okPrecioMin && okPrecioMax;
  }).sort((a, b) => {
    const ca = a.caracteristicas || {}, cb = b.caracteristicas || {};
    switch (ordenacion) {
      case 'precio_asc':  return (a.precio ?? 0) - (b.precio ?? 0);
      case 'precio_desc': return (b.precio ?? 0) - (a.precio ?? 0);
      case 'fecha_asc':   return new Date(a.fecha_publicacion || a.created_at) - new Date(b.fecha_publicacion || b.created_at);
      case 'fecha_desc':  return new Date(b.fecha_publicacion || b.created_at) - new Date(a.fecha_publicacion || a.created_at);
      case 'crm_desc':    return new Date(b.created_at) - new Date(a.created_at);
      case 'crm_asc':     return new Date(a.created_at) - new Date(b.created_at);
      case 'tipo':        return (ca.tipo_propiedad || '').localeCompare(cb.tipo_propiedad || '');
      default:            return 0;
    }
  });

  const porBusqueda  = busquedas.map(b => ({ ...b, items: resFiltrados.filter(r => r.busqueda_id === b.id) }));
  const sinBusqueda  = resFiltrados.filter(r => !r.busqueda_id);

  const testearBusqueda = async id => {
    setTesteando(e => ({ ...e, [id]: true }));
    setTestResultados(s => ({ ...s, [id]: null }));
    try {
      const r = await api.get(`/scraping/test-busqueda/${id}`);
      setTestResultados(s => ({ ...s, [id]: r.data }));
    } catch (e) {
      setTestResultados(s => ({ ...s, [id]: { recomendacion: '❌ Error: ' + e.message } }));
    } finally {
      setTesteando(e => ({ ...e, [id]: false }));
    }
  };

  const toggleSeleccion = id => setSeleccionados(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleTodos     = () => seleccionados.size === resFiltrados.length ? setSeleccionados(new Set()) : setSeleccionados(new Set(resFiltrados.map(r => r.id)));

  // ── Estilos base ──────────────────────────────────────────────────────────────
  const btnFiltro = activo => ({
    padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
    backgroundColor: activo ? t.accentBlue : t.bgCard,
    color:           activo ? '#fff'       : t.textMuted,
  });

  return (
    <div style={{ minHeight:'100vh', backgroundColor:t.bgApp, color:t.textPrimary }}>

      {/* ── Barra superior ── */}
      <div style={{
        position:'sticky', top:56, zIndex:20, borderBottom:`1px solid ${t.border}`,
        backgroundColor: t.bgPanel, padding:'8px 20px',
        display:'flex', flexDirection:'column', gap:8,
      }}>
        {/* Fila 1: título + filtros principales + acciones */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h1 style={{ fontSize:16, fontWeight:800, margin:0 }}>🕷️ Pruebas Scraping</h1>
            <span style={{ fontSize:12, color:t.textMuted }}>
              {resFiltrados.length} mostrados
              {totalResultados > resultados.length && (
                <span style={{ color: t.accentBlue }}> · {totalResultados} en total</span>
              )}
            </span>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>

            {/* Filtro anunciante */}
            <div style={{ display:'flex', borderRadius:8, overflow:'hidden', border:`1px solid ${t.border}` }}>
              {[['todos','Todos'],['Particular','Particulares'],['Agencia','Agencias']].map(([v, l]) => (
                <button key={v} onClick={() => setFiltroAnunciante(v)} style={btnFiltro(filtroAnunciante === v)}>{l}</button>
              ))}
            </div>

            {/* Filtro estado */}
            <div style={{ display:'flex', borderRadius:8, overflow:'hidden', border:`1px solid ${t.border}` }}>
              {[['activo','✅ Activos'],['eliminado','❌ Retirados'],['todos','Todos']].map(([v, l]) => (
                <button key={v} onClick={() => setFiltroEstado(v)} style={btnFiltro(filtroEstado === v)}>{l}</button>
              ))}
            </div>

            {/* Vista */}
            <div style={{ display:'flex', borderRadius:8, overflow:'hidden', border:`1px solid ${t.border}` }}>
              {[['pipeline','⬡ Pipeline'],['tabla','☰ Tabla']].map(([v, l]) => (
                <button key={v} onClick={() => setVista(v)} style={btnFiltro(vista === v)}>{l}</button>
              ))}
            </div>

            <button onClick={cargar} style={{ padding:'5px 12px', fontSize:12, backgroundColor:'transparent', color:t.textMuted, border:`1px solid ${t.border}`, borderRadius:8, cursor:'pointer' }}>
              ↻ Actualizar
            </button>

            {verPruebas && (
            <button onClick={() => setShowNuevaIdealista(s => !s)}
              style={{ padding:'5px 12px', fontSize:12, backgroundColor:'rgba(14,165,233,0.1)', color:'#0ea5e9', border:'1px solid rgba(14,165,233,0.4)', borderRadius:8, cursor:'pointer', fontWeight:600 }}>
              + Idealista
            </button>
            )}

          {/* Enriquecimiento retroactivo */}
          {verLanzar && (enriqueciendo ? (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:11, color:t.accentBlue }}>
                🔍 {enriqEstado?.msg || 'Iniciando…'}
              </span>
              <button onClick={cancelarEnriquecimiento} style={{ padding:'4px 10px', fontSize:11, backgroundColor:'transparent', color:t.accentRed, border:`1px solid ${t.accentRed}`, borderRadius:6, cursor:'pointer' }}>
                ✕ Cancelar
              </button>
            </div>
          ) : (
            <div style={{ display:'flex', gap:6 }}>
              <button
                onClick={iniciarEnriquecimientoParticulares}
                title={`Obtener dirección exacta y teléfono de los ${nParticulares} particulares (rápido y barato)`}
                style={{ padding:'5px 12px', fontSize:12, backgroundColor:'rgba(34,197,94,0.1)', color:'#22c55e', border:'1px solid #22c55e', borderRadius:8, cursor:'pointer', fontWeight:600 }}>
                📍 Enriquecer Particulares {nParticulares > 0 ? `(${nParticulares})` : ''}
              </button>
              <button
                onClick={iniciarEnriquecimiento}
                title="Visita la página de detalle de cada propiedad para obtener dirección completa y detectar particulares"
                style={{ padding:'5px 12px', fontSize:12, backgroundColor:'transparent', color:'#f59e0b', border:'1px solid #f59e0b', borderRadius:8, cursor:'pointer' }}>
                🔍 Enriquecer todos
              </button>
            </div>
          ))}
          </div>{/* fin fila 1 derecha */}
        </div>{/* fin fila 1 */}

        {/* Barra de progreso enriquecimiento portales */}
        {Object.entries(enrichPortalSt).some(([,s]) => s && s.estado !== null) && (
          <div style={{ display:'flex', gap:8, alignItems:'center', padding:'6px 10px', backgroundColor:'rgba(245,158,11,0.08)', borderRadius:8, border:'1px solid rgba(245,158,11,0.2)', flexWrap:'wrap' }}>
            <span style={{ fontSize:11, fontWeight:600, color:'#f59e0b' }}>Enriquecimiento portales:</span>
            {[
              { key:'idealista',  label:'Idealista',  color:'#0ea5e9' },
              { key:'pisos',      label:'Pisos.com',  color:'#a855f7' },
              { key:'habitaclia', label:'Habitaclia', color:'#06b6d4' },
            ].map(({ key, label, color }) => {
              const s = enrichPortalSt[key];
              if (!s) return null;
              const isRun = s.estado === 'running';
              const isDone = s.estado === 'completado';
              const isErr = s.estado === 'error';
              const pct = s.total && s.total !== '?' ? Math.round((s.procesados / s.total) * 100) : null;
              return (
                <div key={key} style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ fontSize:11, color, fontWeight:600 }}>{label}:</span>
                  {isRun && (
                    <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <div style={{ width:80, height:6, backgroundColor:'rgba(255,255,255,0.1)', borderRadius:3, overflow:'hidden' }}>
                        {pct !== null && <div style={{ width:`${pct}%`, height:'100%', backgroundColor:color, transition:'width 0.5s', borderRadius:3 }} />}
                      </div>
                      <span style={{ fontSize:10, color:'#f59e0b' }}>{s.procesados}/{s.total === '?' ? '...' : s.total}</span>
                    </div>
                  )}
                  {isDone && <span style={{ fontSize:11, color:'#22c55e', fontWeight:700 }}>✓ {s.conDatos} enriquecidas</span>}
                  {isErr  && <span style={{ fontSize:11, color:'#ef4444' }}>✗ Error</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Fila 2: filtros de detalle + ordenación */}
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', paddingTop:2 }}>

          {/* Tipo de propiedad */}
          <select
            value={filtroTipo}
            onChange={e => setFiltroTipo(e.target.value)}
            style={{ padding:'4px 8px', fontSize:11, borderRadius:6, border:`1px solid ${t.border}`, backgroundColor:t.bgCard, color: filtroTipo !== 'todos' ? t.accentBlue : t.textMuted, cursor:'pointer' }}>
            <option value="todos">Tipo: todos</option>
            {tiposDisponibles.map(tipo => (
              <option key={tipo} value={tipo}>{traducirTipo(tipo)}</option>
            ))}
          </select>

          {/* Dormitorios */}
          <select
            value={filtroDorm}
            onChange={e => setFiltroDorm(e.target.value)}
            style={{ padding:'4px 8px', fontSize:11, borderRadius:6, border:`1px solid ${t.border}`, backgroundColor:t.bgCard, color: filtroDorm !== 'todos' ? t.accentBlue : t.textMuted, cursor:'pointer' }}>
            <option value="todos">Dorm: todos</option>
            {['1','2','3','4','5+'].map(d => <option key={d} value={d}>{d} dorm.</option>)}
          </select>

          {/* Precio mín */}
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <span style={{ fontSize:11, color:t.textMuted }}>€</span>
            <input
              type="number" placeholder="Precio mín"
              value={filtroPrecioMin}
              onChange={e => setFiltroPrecioMin(e.target.value)}
              style={{ width:100, padding:'4px 7px', fontSize:11, borderRadius:6, border:`1px solid ${filtroPrecioMin ? t.accentBlue : t.border}`, backgroundColor:t.bgCard, color:t.textPrimary }}
            />
            <span style={{ fontSize:11, color:t.textMuted }}>–</span>
            <input
              type="number" placeholder="Precio máx"
              value={filtroPrecioMax}
              onChange={e => setFiltroPrecioMax(e.target.value)}
              style={{ width:100, padding:'4px 7px', fontSize:11, borderRadius:6, border:`1px solid ${filtroPrecioMax ? t.accentBlue : t.border}`, backgroundColor:t.bgCard, color:t.textPrimary }}
            />
          </div>

          {/* Ordenación */}
          <select
            value={ordenacion}
            onChange={e => setOrdenacion(e.target.value)}
            style={{ padding:'4px 8px', fontSize:11, borderRadius:6, border:`1px solid ${t.border}`, backgroundColor:t.bgCard, color:t.textMuted, cursor:'pointer' }}>
            <option value="fecha_desc">↓ Publicación más reciente (portal)</option>
            <option value="fecha_asc">↑ Publicación más antigua (portal)</option>
            <option value="crm_desc">↓ Importación CRM más reciente</option>
            <option value="crm_asc">↑ Importación CRM más antigua</option>
            <option value="precio_asc">↑ Precio menor</option>
            <option value="precio_desc">↓ Precio mayor</option>
            <option value="tipo">A–Z Tipo</option>
          </select>

          {/* Limpiar filtros de detalle */}
          {(filtroTipo !== 'todos' || filtroDorm !== 'todos' || filtroPrecioMin !== '' || filtroPrecioMax !== '') && (
            <button
              onClick={() => { setFiltroTipo('todos'); setFiltroDorm('todos'); setFiltroPrecioMin(''); setFiltroPrecioMax(''); }}
              style={{ padding:'4px 9px', fontSize:11, backgroundColor:'rgba(239,68,68,0.1)', color:t.accentRed, border:`1px solid ${t.accentRed}`, borderRadius:6, cursor:'pointer' }}>
              ✕ Limpiar filtros
            </button>
          )}
        </div>{/* fin fila 2 */}

        {/* Fila 3: panel nueva búsqueda Idealista */}
        {showNuevaIdealista && (
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', padding:'8px 10px', backgroundColor:'rgba(14,165,233,0.06)', borderRadius:8, border:'1px solid rgba(14,165,233,0.25)' }}>
            <span style={{ fontSize:11, fontWeight:700, color:'#0ea5e9' }}>Nueva búsqueda Idealista:</span>
            <input
              value={nuevaIdealistaNombre}
              onChange={e => setNuevaIdealistaNombre(e.target.value)}
              placeholder="Nombre (ej: Denia - Idealista)"
              style={{ padding:'4px 8px', fontSize:11, borderRadius:6, border:`1px solid ${t.border}`, backgroundColor:t.bgCard, color:t.textPrimary, width:180 }}
            />
            <textarea
              value={nuevaIdealistaUrl}
              onChange={e => setNuevaIdealistaUrl(e.target.value)}
              placeholder={"URL(s) Idealista, una por línea:\nhttps://www.idealista.com/venta-viviendas/denia-alicante/\nhttps://www.idealista.com/venta-viviendas/javea-alicante/"}
              rows={3}
              style={{ padding:'4px 8px', fontSize:11, borderRadius:6, border:`1px solid ${t.border}`, backgroundColor:t.bgCard, color:t.textPrimary, width:400, resize:'vertical', fontFamily:'inherit' }}
            />
            <button onClick={crearBusquedaIdealista}
              style={{ padding:'4px 12px', fontSize:11, backgroundColor:'#0ea5e9', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontWeight:700 }}>
              Crear
            </button>
            <button onClick={() => setShowNuevaIdealista(false)}
              style={{ padding:'4px 8px', fontSize:11, backgroundColor:'transparent', color:t.textMuted, border:`1px solid ${t.border}`, borderRadius:6, cursor:'pointer' }}>
              Cancelar
            </button>
          </div>
        )}
      </div>{/* fin barra superior */}

      {/* ── Cuerpo ── */}
      {cargando ? (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:260 }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ width:32, height:32, border:`3px solid ${t.accentBlue}`, borderTopColor:'transparent', borderRadius:'50%', margin:'0 auto 10px', animation:'spin 0.8s linear infinite' }} />
            <p style={{ color:t.textMuted, fontSize:13 }}>Cargando resultados…</p>
          </div>
        </div>

      ) : vista === 'pipeline' ? (
        // ── Vista Pipeline ─────────────────────────────────────────────────────
        <div style={{ display:'flex', gap:14, padding:16, overflowX:'auto', minHeight:'calc(100vh - 56px)', alignItems:'flex-start' }}>
          {[...porBusqueda, ...(sinBusqueda.length ? [{ id:null, nombre:'Sin búsqueda', items:sinBusqueda }] : [])].map(col => (
            <div key={col.id || 'sin'} style={{ flexShrink:0, width:272 }}>
              {/* Cabecera columna */}
              <div style={{ backgroundColor:t.bgCard, border:`1px solid ${t.border}`, borderRadius:10, padding:'10px 12px', marginBottom:10 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <p style={{ fontSize:12, fontWeight:700, color:t.textPrimary, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:130 }}>{col.nombre}</p>
                      {(col.portal && col.portal !== 'fotocasa') && (() => {
                        const BADGE_STYLE = {
                          idealista:   { bg:'rgba(14,165,233,0.15)',  color:'#0ea5e9', label:'I'  },
                          pisos:       { bg:'rgba(168,85,247,0.15)',  color:'#a855f7', label:'P'  },
                          kyero:       { bg:'rgba(16,185,129,0.15)',  color:'#10b981', label:'K'  },
                          habitaclia:  { bg:'rgba(6,182,212,0.15)',   color:'#06b6d4', label:'H'  },
                          milanuncios: { bg:'rgba(245,158,11,0.15)',  color:'#f59e0b', label:'M'  },
                          thinkspain:  { bg:'rgba(236,72,153,0.15)',  color:'#ec4899', label:'TS' },
                          todopisos:   { bg:'rgba(132,204,22,0.15)',  color:'#84cc16', label:'TP' },
                        };
                        const bs = BADGE_STYLE[col.portal];
                        if (!bs) return null;
                        return <span style={{ fontSize:9, padding:'1px 5px', backgroundColor:bs.bg, color:bs.color, borderRadius:4, fontWeight:700, flexShrink:0 }}>{bs.label}</span>;
                      })()}
                    </div>
                    <p style={{ fontSize:10, color:t.textMuted, margin:'2px 0 0' }}>
                      {col.id && totalPorBusqueda[col.id] != null
                        ? `${totalPorBusqueda[col.id]} propiedades`
                        : `${col.items.length} propiedades`}
                    </p>
                  </div>
                  {col.id && (
                    <div style={{ display:'flex', gap:4 }}>
                      {col.portal === 'fotocasa' && <>
                        {verPruebas && (
                        <button onClick={() => testearBusqueda(col.id)} disabled={testeando[col.id]} title="Diagnóstico rápido (sin guardar)"
                          style={{ padding:'4px 7px', backgroundColor:'rgba(249,115,22,0.12)', color:'#f97316', border:'none', borderRadius:7, cursor:'pointer', fontSize:11, opacity: testeando[col.id] ? 0.5 : 1 }}>
                          {testeando[col.id] ? '⏳' : '🔍'}
                        </button>
                        )}
                        {verLanzar && <>
                        <button onClick={() => ejecutarBusqueda(col.id)} disabled={ejecutando[col.id]} title="Scraping normal (31 props)"
                          style={{ padding:'4px 7px', backgroundColor:'rgba(34,197,94,0.12)', color:t.accentGreen, border:'none', borderRadius:7, cursor:'pointer', fontSize:11, opacity: ejecutando[col.id] ? 0.5 : 1 }}>
                          {ejecutando[col.id] ? '⏳' : '▶'}
                        </button>
                        {biseccionEstados[col.id]?.estado === 'running' ? (
                          <button onClick={() => cancelarImportMasivo(col.id)} title="Cancelar import masivo"
                            style={{ padding:'4px 7px', backgroundColor:'rgba(239,68,68,0.12)', color:'#ef4444', border:'none', borderRadius:7, cursor:'pointer', fontSize:11 }}>
                            ⏹
                          </button>
                        ) : (
                          <button onClick={() => iniciarImportMasivo(col.id, col.nombre)} title="Import masivo Fotocasa: bisección por precio"
                            style={{ padding:'4px 7px', backgroundColor:'rgba(168,85,247,0.12)', color:'#a855f7', border:'none', borderRadius:7, cursor:'pointer', fontSize:11 }}>
                            ⬇
                          </button>
                        )}
                        </>}
                      </>}
                      {col.portal === 'idealista' && verLanzar && (() => {
                        const urls = col.extras?.urls_idealista || (col.extras?.url_idealista ? [col.extras.url_idealista] : []);
                        const est  = idealistaEstados[col.id];
                        return est?.estado === 'scraping' ? (
                          <button onClick={() => cancelarImportIdealista(col.id)} title="Cancelar import Idealista"
                            style={{ padding:'4px 7px', backgroundColor:'rgba(239,68,68,0.12)', color:'#ef4444', border:'none', borderRadius:7, cursor:'pointer', fontSize:11 }}>
                            ⏹
                          </button>
                        ) : (
                          <>
                            <button onClick={() => iniciarImportIdealista(col.id, col, col.nombre)}
                              title={urls.length ? `Import Idealista: ${urls.length} URL(s)` : 'Sin URL configurada'}
                              style={{ padding:'4px 7px', backgroundColor:'rgba(14,165,233,0.12)', color:'#0ea5e9', border:'none', borderRadius:7, cursor:'pointer', fontSize:11, opacity: urls.length ? 1 : 0.4 }}>
                              ⬇ {urls.length > 1 ? `(${urls.length})` : ''}
                            </button>
                            {(() => {
                              const est = enrichPortalSt?.idealista;
                              const running = est?.estado === 'running';
                              const done = est?.estado === 'completado';
                              const err = est?.estado === 'error';
                              return (
                                <button
                                  disabled={running}
                                  onClick={async () => {
                                    if (!(await dialogs.confirm('Enriquecer propiedades Idealista (nombre, dirección, características)?\n\nVisitará la ficha de cada propiedad sin datos completos. Puede tardar 20-40 min.'))) return;
                                    lanzarEnriquecimientoPortal('idealista', '/import/idealista/enriquecer-particulares?limite=200');
                                  }}
                                  title={running ? `Enriqueciendo Idealista: ${est.procesados}/${est.total}` : 'Enriquecer Idealista (nombre, dirección, características)'}
                                  style={{ padding:'4px 7px', backgroundColor: done ? 'rgba(34,197,94,0.15)' : err ? 'rgba(239,68,68,0.12)' : 'rgba(14,165,233,0.12)', color: done ? '#22c55e' : err ? '#ef4444' : '#0ea5e9', border:'none', borderRadius:7, cursor: running ? 'not-allowed' : 'pointer', fontSize:11, opacity: running ? 0.7 : 1, minWidth: 38 }}>
                                  {running ? `⏳${est.procesados}/${est.total}` : done ? `✓${est.conDatos}` : err ? '✗' : '📞'}
                                </button>
                              );
                            })()}
                          </>
                        );
                      })()}
                      {col.portal === 'pisos' && verLanzar && (() => {
                        const urls = col.extras?.urls_pisos || (col.extras?.url_pisos ? [col.extras.url_pisos] : []);
                        const est  = pisosEstados[col.id];
                        return est?.estado === 'scraping' ? (
                          <button onClick={() => cancelarImportPisos(col.id)} title="Cancelar import Pisos.com"
                            style={{ padding:'4px 7px', backgroundColor:'rgba(239,68,68,0.12)', color:'#ef4444', border:'none', borderRadius:7, cursor:'pointer', fontSize:11 }}>
                            ⏹
                          </button>
                        ) : (
                          <>
                            <button onClick={() => iniciarImportPisos(col.id, col, col.nombre)}
                              title={urls.length ? `Import Pisos.com: ${urls.length} URL(s)` : 'Sin URL configurada'}
                              style={{ padding:'4px 7px', backgroundColor:'rgba(168,85,247,0.12)', color:'#a855f7', border:'none', borderRadius:7, cursor:'pointer', fontSize:11, opacity: urls.length ? 1 : 0.4 }}>
                              ⬇ {urls.length > 1 ? `(${urls.length})` : ''}
                            </button>
                            {(() => {
                              const est = enrichPortalSt?.pisos;
                              const running = est?.estado === 'running';
                              const done = est?.estado === 'completado';
                              const err = est?.estado === 'error';
                              return (
                                <button
                                  disabled={running}
                                  onClick={async () => {
                                    if (!(await dialogs.confirm('Enriquecer propiedades Pisos.com (título, nombre agencia, características)?\n\nVisitará la ficha de cada propiedad con datos incompletos. Puede tardar 20-40 min.'))) return;
                                    lanzarEnriquecimientoPortal('pisos', '/import/pisos/enriquecer-particulares?limite=200');
                                  }}
                                  title={running ? `Enriqueciendo Pisos.com: ${est.procesados}/${est.total}` : 'Enriquecer Pisos.com (título, nombre, características)'}
                                  style={{ padding:'4px 7px', backgroundColor: done ? 'rgba(34,197,94,0.15)' : err ? 'rgba(239,68,68,0.12)' : 'rgba(168,85,247,0.12)', color: done ? '#22c55e' : err ? '#ef4444' : '#a855f7', border:'none', borderRadius:7, cursor: running ? 'not-allowed' : 'pointer', fontSize:11, opacity: running ? 0.7 : 1, minWidth: 38 }}>
                                  {running ? `⏳${est.procesados}/${est.total}` : done ? `✓${est.conDatos}` : err ? '✗' : '📞'}
                                </button>
                              );
                            })()}
                          </>
                        );
                      })()}
                      {col.portal === 'habitaclia' && verLanzar && (() => {
                        const urls = col.extras?.urls_habitaclia || (col.extras?.url_habitaclia ? [col.extras.url_habitaclia] : []);
                        const est  = habitacliaEstados[col.id];
                        return est?.estado === 'scraping' ? (
                          <button onClick={() => cancelarImportHabitaclia(col.id)} title="Cancelar import Habitaclia"
                            style={{ padding:'4px 7px', backgroundColor:'rgba(239,68,68,0.12)', color:'#ef4444', border:'none', borderRadius:7, cursor:'pointer', fontSize:11 }}>
                            ⏹
                          </button>
                        ) : (
                          <>
                            <button onClick={() => iniciarImportHabitaclia(col.id, col, col.nombre)}
                              title={urls.length ? `Import Habitaclia: ${urls.length} URL(s)` : 'Sin URL configurada'}
                              style={{ padding:'4px 7px', backgroundColor:'rgba(6,182,212,0.12)', color:'#06b6d4', border:'none', borderRadius:7, cursor:'pointer', fontSize:11, opacity: urls.length ? 1 : 0.4 }}>
                              ⬇ {urls.length > 1 ? `(${urls.length})` : ''}
                            </button>
                            {(() => {
                              const est = enrichPortalSt?.habitaclia;
                              const running = est?.estado === 'running';
                              const done = est?.estado === 'completado';
                              const err = est?.estado === 'error';
                              return (
                                <button
                                  disabled={running}
                                  onClick={async () => {
                                    if (!(await dialogs.confirm('Enriquecer propiedades de Habitaclia (teléfono, dirección, nombre)?\n\nVisitará la ficha de cada propiedad sin datos completos. Puede tardar 20-40 min.'))) return;
                                    lanzarEnriquecimientoPortal('habitaclia', '/import/habitaclia/enriquecer?limite=200');
                                  }}
                                  title={running ? `Enriqueciendo Habitaclia: ${est.procesados}/${est.total}` : 'Enriquecer Habitaclia (teléfono, dirección, nombre)'}
                                  style={{ padding:'4px 7px', backgroundColor: done ? 'rgba(34,197,94,0.15)' : err ? 'rgba(239,68,68,0.12)' : 'rgba(6,182,212,0.12)', color: done ? '#22c55e' : err ? '#ef4444' : '#06b6d4', border:'none', borderRadius:7, cursor: running ? 'not-allowed' : 'pointer', fontSize:11, opacity: running ? 0.7 : 1, minWidth: 38 }}>
                                  {running ? `⏳${est.procesados}/${est.total}` : done ? `✓${est.conDatos}` : err ? '✗' : '📞'}
                                </button>
                              );
                            })()}
                          </>
                        );
                      })()}
                      {['kyero','milanuncios','thinkspain','todopisos'].includes(col.portal) && (() => {
                        const COLOR = { kyero:'#10b981', milanuncios:'#f59e0b', thinkspain:'#ec4899', todopisos:'#84cc16' };
                        const c = COLOR[col.portal];
                        const extrasKey = `urls_${col.portal}`;
                        const firstKey  = `url_${col.portal}`;
                        const urls = col.extras?.[extrasKey] || (col.extras?.[firstKey] ? [col.extras[firstKey]] : []);
                        return (
                          <button disabled title={`Import ${col.portal} — próximamente`}
                            style={{ padding:'4px 7px', backgroundColor:`rgba(0,0,0,0.06)`, color:c, border:'none', borderRadius:7, cursor:'not-allowed', fontSize:11, opacity:0.5 }}>
                            ⬇ {urls.length > 1 ? `(${urls.length})` : ''}
                          </button>
                        );
                      })()}
                    </div>
                  )}
                </div>
                {/* Barra de progreso en tiempo real */}
                {col.id && jobEstados[col.id] && jobEstados[col.id].estado !== 'inactivo' && (() => {
                  const je = jobEstados[col.id];
                  const pct = je.total > 0 ? Math.round((je.procesados / je.total) * 100) : null;
                  const colorEst = je.estado === 'terminado' ? t.accentGreen : je.estado === 'error' ? t.accentRed : t.accentBlue;
                  return (
                    <div style={{ marginTop:8 }}>
                      {pct !== null && (
                        <div style={{ height:4, backgroundColor:t.border, borderRadius:2, overflow:'hidden', marginBottom:4 }}>
                          <div style={{ height:'100%', width:pct+'%', backgroundColor:colorEst, borderRadius:2, transition:'width 0.3s ease' }} />
                        </div>
                      )}
                      <p style={{ fontSize:10, color:colorEst, margin:0, lineHeight:1.3 }}>
                        {je.estado === 'terminado' ? '✅ ' : je.estado === 'error' ? '❌ ' : '⏳ '}
                        {je.msg?.length > 50 ? je.msg.substring(0,50)+'…' : (je.msg || je.estado)}
                      </p>
                      {je.nuevos > 0 && (
                        <p style={{ fontSize:10, color:t.accentGreen, fontWeight:700, margin:'2px 0 0' }}>+{je.nuevos} nuevos</p>
                      )}
                    </div>
                  );
                })()}

                {/* Panel import Idealista */}
                {col.id && col.portal === 'idealista' && idealistaEstados[col.id] && (() => {
                  const est = idealistaEstados[col.id];
                  if (!est || est.estado === 'sin_datos') return null;
                  const color = est.estado === 'completado' ? t.accentGreen : est.estado === 'error' ? t.accentRed : '#0ea5e9';
                  const pct = est.totalPaginas > 0 ? Math.round((est.pagina / est.totalPaginas) * 100) : null;
                  return (
                    <div style={{ marginTop:8, padding:'8px', borderRadius:7, backgroundColor:'rgba(14,165,233,0.07)', border:'1px solid rgba(14,165,233,0.25)' }}>
                      <p style={{ fontSize:10, fontWeight:700, color, margin:'0 0 3px' }}>
                        {est.estado === 'completado' ? '✅' : est.estado === 'error' ? '❌' : est.estado === 'cancelado' ? '⏹' : '⬇'} Import Idealista
                      </p>
                      {est.urls?.length > 1 && (
                        <p style={{ fontSize:9, color:'#0ea5e9', margin:'0 0 2px' }}>
                          Pueblo {(est.urlIdx || 0)+1}/{est.urls.length}: {est.urlActual?.split('/').filter(Boolean).pop() ?? ''}
                        </p>
                      )}
                      {pct !== null && est.estado === 'scraping' && (
                        <div style={{ height:3, backgroundColor:t.border, borderRadius:2, overflow:'hidden', marginBottom:4 }}>
                          <div style={{ height:'100%', width:pct+'%', backgroundColor:'#0ea5e9', borderRadius:2, transition:'width 0.4s ease' }} />
                        </div>
                      )}
                      <p style={{ fontSize:9, color:t.textMuted, margin:0 }}>
                        Pág. {est.pagina || 0} · {est.totalResultados || 0} anuncios detectados
                      </p>
                      {(est.guardados > 0 || est.duplicados > 0) && (
                        <p style={{ fontSize:9, color:'#0ea5e9', margin:'2px 0 0', fontWeight:700 }}>
                          +{est.guardados || 0} guardados · {est.duplicados || 0} duplicados · {est.errores || 0} errores
                        </p>
                      )}
                      {est.estado === 'error' && <p style={{ fontSize:9, color:t.accentRed, margin:'2px 0 0' }}>{est.error}</p>}
                    </div>
                  );
                })()}

                {/* Panel import Pisos.com */}
                {col.id && col.portal === 'pisos' && pisosEstados[col.id] && (() => {
                  const est = pisosEstados[col.id];
                  if (!est || est.estado === 'sin_datos') return null;
                  const color = est.estado === 'completado' ? t.accentGreen : est.estado === 'error' ? t.accentRed : '#a855f7';
                  const pct = est.totalPaginas > 0 ? Math.round((est.pagina / est.totalPaginas) * 100) : null;
                  return (
                    <div style={{ marginTop:8, padding:'8px', borderRadius:7, backgroundColor:'rgba(168,85,247,0.07)', border:'1px solid rgba(168,85,247,0.25)' }}>
                      <p style={{ fontSize:10, fontWeight:700, color, margin:'0 0 3px' }}>
                        {est.estado === 'completado' ? '✅' : est.estado === 'error' ? '❌' : est.estado === 'cancelado' ? '⏹' : '⬇'} Import Pisos.com
                      </p>
                      {est.urls?.length > 1 && (
                        <p style={{ fontSize:9, color:'#a855f7', margin:'0 0 2px' }}>
                          URL {(est.urlIdx || 0)+1}/{est.urls.length}: {est.urlActual?.split('/').filter(Boolean).pop() ?? ''}
                        </p>
                      )}
                      {pct !== null && est.estado === 'scraping' && (
                        <div style={{ height:3, backgroundColor:t.border, borderRadius:2, overflow:'hidden', marginBottom:4 }}>
                          <div style={{ height:'100%', width:pct+'%', backgroundColor:'#a855f7', borderRadius:2, transition:'width 0.4s ease' }} />
                        </div>
                      )}
                      <p style={{ fontSize:9, color:t.textMuted, margin:0 }}>
                        Pág. {est.pagina || 0} · {est.totalResultados || 0} anuncios detectados
                      </p>
                      {(est.guardados > 0 || est.duplicados > 0) && (
                        <p style={{ fontSize:9, color:'#a855f7', margin:'2px 0 0', fontWeight:700 }}>
                          +{est.guardados || 0} guardados · {est.duplicados || 0} duplicados · {est.errores || 0} errores
                        </p>
                      )}
                      {est.estado === 'error' && <p style={{ fontSize:9, color:t.accentRed, margin:'2px 0 0' }}>{est.error}</p>}
                    </div>
                  );
                })()}

                {/* Panel import Habitaclia */}
                {col.id && col.portal === 'habitaclia' && habitacliaEstados[col.id] && (() => {
                  const est = habitacliaEstados[col.id];
                  if (!est || est.estado === 'sin_datos') return null;
                  const color = est.estado === 'completado' ? t.accentGreen : est.estado === 'error' ? t.accentRed : '#06b6d4';
                  const pct = est.totalPaginas > 0 ? Math.round((est.pagina / est.totalPaginas) * 100) : null;
                  return (
                    <div style={{ marginTop:8, padding:'8px', borderRadius:7, backgroundColor:'rgba(6,182,212,0.07)', border:'1px solid rgba(6,182,212,0.25)' }}>
                      <p style={{ fontSize:10, fontWeight:700, color, margin:'0 0 3px' }}>
                        {est.estado === 'completado' ? '✅' : est.estado === 'error' ? '❌' : est.estado === 'cancelado' ? '⏹' : '⬇'} Import Habitaclia
                      </p>
                      {est.urls?.length > 1 && (
                        <p style={{ fontSize:9, color:'#06b6d4', margin:'0 0 2px' }}>
                          URL {(est.urlIdx || 0)+1}/{est.urls.length}: {est.urlActual?.split('/').filter(Boolean).pop() ?? ''}
                        </p>
                      )}
                      {pct !== null && est.estado === 'scraping' && (
                        <div style={{ height:3, backgroundColor:t.border, borderRadius:2, overflow:'hidden', marginBottom:4 }}>
                          <div style={{ height:'100%', width:pct+'%', backgroundColor:'#06b6d4', borderRadius:2, transition:'width 0.4s ease' }} />
                        </div>
                      )}
                      <p style={{ fontSize:9, color:t.textMuted, margin:0 }}>
                        Pág. {est.pagina || 0}/{est.totalPaginas || '?'} · {est.totalResultados || 0} anuncios detectados
                      </p>
                      {est.guardados > 0 && (
                        <p style={{ fontSize:9, color:'#06b6d4', margin:'2px 0 0', fontWeight:700 }}>
                          +{est.guardados || 0} guardados
                        </p>
                      )}
                      {est.estado === 'error' && <p style={{ fontSize:9, color:t.accentRed, margin:'2px 0 0' }}>{est.error}</p>}
                    </div>
                  );
                })()}

                {/* Panel bisección import */}
                {col.id && biseccionEstados[col.id] && biseccionEstados[col.id].estado !== 'idle' && (() => {
                  const bs = biseccionEstados[col.id];
                  const colorBs = bs.estado === 'completed' ? t.accentGreen : bs.estado === 'error' ? t.accentRed : '#a855f7';
                  return (
                    <div style={{ marginTop:8, padding:'8px', borderRadius:7, backgroundColor:'rgba(168,85,247,0.07)', border:'1px solid rgba(168,85,247,0.25)' }}>
                      <p style={{ fontSize:10, fontWeight:700, color:colorBs, margin:'0 0 3px' }}>
                        {bs.estado === 'completed' ? '✅' : bs.estado === 'error' ? '❌' : bs.estado === 'cancelled' ? '⏹' : '⬇'} Import masivo
                      </p>
                      <p style={{ fontSize:9, color:t.textMuted, margin:0, lineHeight:1.4, wordBreak:'break-all' }}>
                        {bs.msg?.length > 60 ? bs.msg.substring(0, 60) + '…' : bs.msg}
                      </p>
                      {(bs.itemsSaved > 0 || bs.requestsMade > 0) && (
                        <p style={{ fontSize:9, color:'#a855f7', margin:'2px 0 0', fontWeight:700 }}>
                          +{bs.itemsSaved || 0} guardados · {bs.requestsMade || 0} requests · {bs.rangesProcessed || 0} rangos
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Panel diagnóstico (botón 🔍) */}
                {col.id && testResultados[col.id] && (() => {
                  const tr = testResultados[col.id];
                  const ok = tr.checks?.[0]?.ok;
                  const c0 = tr.checks?.[0] || {};
                  const colorRec = tr.recomendacion?.startsWith('✅') ? t.accentGreen
                                 : tr.recomendacion?.startsWith('⚠️') ? '#f97316'
                                 : t.accentRed;
                  return (
                    <div style={{ marginTop:8, padding:'8px', borderRadius:7, backgroundColor: ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', border:`1px solid ${ok ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
                      <p style={{ fontSize:10, fontWeight:700, color:colorRec, margin:'0 0 4px', lineHeight:1.4 }}>{tr.recomendacion}</p>
                      {c0.url && (
                        <p style={{ fontSize:9, color:t.textMuted, margin:'2px 0', wordBreak:'break-all' }}>
                          URL: <a href={c0.url} target="_blank" rel="noopener noreferrer" style={{ color:t.accentBlue }}>{c0.url}</a>
                        </p>
                      )}
                      {c0.http_status && <p style={{ fontSize:9, color:t.textMuted, margin:'1px 0' }}>HTTP: {c0.http_status} · HTML: {c0.html_length?.toLocaleString()} chars</p>}
                      {c0.scripts_con_initial_props != null && <p style={{ fontSize:9, color:t.textMuted, margin:'1px 0' }}>Scripts con __INITIAL_PROPS__: {c0.scripts_con_initial_props}</p>}
                      {c0.real_estates_count != null && <p style={{ fontSize:9, color:t.textMuted, margin:'1px 0' }}>Propiedades en listado: {c0.real_estates_count}</p>}
                      {c0.result_keys?.length > 0 && <p style={{ fontSize:9, color:t.textMuted, margin:'1px 0' }}>Claves result: {c0.result_keys.join(', ')}</p>}
                      {c0.error_detalle && <p style={{ fontSize:9, color:t.accentRed, margin:'2px 0', wordBreak:'break-all' }}>{c0.error_detalle?.substring(0, 200)}</p>}
                      {c0.sample && <p style={{ fontSize:9, color:t.accentGreen, margin:'2px 0' }}>Muestra: id={c0.sample.id} precio={c0.sample.price} fotos={c0.sample.photosCount}</p>}
                      <button onClick={() => setTestResultados(s => ({ ...s, [col.id]: null }))}
                        style={{ marginTop:4, fontSize:9, color:t.textMuted, background:'none', border:'none', cursor:'pointer', padding:0 }}>× cerrar</button>
                    </div>
                  );
                })()}
              </div>

              {/* Tarjetas */}
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {col.items.length === 0 ? (
                  <div style={{ border:`2px dashed ${t.border}`, borderRadius:10, padding:'24px 12px', textAlign:'center', color:t.textMuted, fontSize:11 }}>Sin resultados</div>
                ) : col.items.map(item => (
                  <TarjetaScraping key={item.id} item={item} t={t} onClick={() => setDetalleItem(item)} />
                ))}
              </div>
            </div>
          ))}

          {busquedas.length === 0 && sinBusqueda.length === 0 && (
            <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:t.textMuted, textAlign:'center' }}>
              <div>
                <p style={{ fontSize:36, marginBottom:8 }}>🕷️</p>
                <p style={{ fontSize:13 }}>No hay búsquedas configuradas.</p>
                <p style={{ fontSize:12 }}>Ve a <strong style={{ color:t.accentBlue }}>Configuración → Scraping</strong> para crear una.</p>
              </div>
            </div>
          )}
        </div>

      ) : (
        // ── Vista Tabla ────────────────────────────────────────────────────────
        <div style={{ padding:20 }}>
          {seleccionados.size > 0 && (
            <div style={{ marginBottom:12, padding:'10px 14px', borderRadius:10, backgroundColor:t.accentBlueLight, border:`1px solid ${t.accentBlueBorder}`, display:'flex', alignItems:'center', gap:14, fontSize:13 }}>
              <span style={{ fontWeight:600, color:t.accentBlue }}>{seleccionados.size} seleccionados</span>
              <button onClick={() => { seleccionados.forEach(id => descartar(id)); setSeleccionados(new Set()); }}
                style={{ color:t.accentRed, background:'none', border:'none', cursor:'pointer', fontSize:12 }}>
                🗑 Descartar seleccionados
              </button>
            </div>
          )}

          <div style={{ backgroundColor:t.bgCard, border:`1px solid ${t.border}`, borderRadius:12, overflow:'hidden' }}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ backgroundColor:t.bgPanel }}>
                    <th style={{ padding:'10px 12px', textAlign:'left', borderBottom:`1px solid ${t.border}` }}>
                      <input type="checkbox" checked={seleccionados.size > 0 && seleccionados.size === resFiltrados.length} onChange={toggleTodos} />
                    </th>
                    {['Foto','Dirección','Tipo','Hab/Baños/M²','Contacto','Teléfono','Portal','Anunciante','Entrada CRM','Acciones'].map(h => (
                      <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:9, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:`1px solid ${t.border}`, whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resFiltrados.length === 0 ? (
                    <tr><td colSpan={11} style={{ padding:'40px', textAlign:'center', color:t.textMuted }}>Sin resultados</td></tr>
                  ) : resFiltrados.map((item, i) => {
                    const car  = item.caracteristicas || {};
                    const foto = item.fotos?.[0];
                    const sel  = seleccionados.has(item.id);
                    return (
                      <tr key={item.id} style={{ borderBottom:`1px solid ${t.borderLight}`, backgroundColor: sel ? t.accentBlueLight : i % 2 === 0 ? 'transparent' : t.bgPanel + '20' }}
                        onMouseEnter={e => { if (!sel) e.currentTarget.style.backgroundColor = t.bgCardHover; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = sel ? t.accentBlueLight : i % 2 === 0 ? 'transparent' : t.bgPanel + '20'; }}
                      >
                        <td style={{ padding:'8px 12px' }}>
                          <input type="checkbox" checked={sel} onChange={() => toggleSeleccion(item.id)} />
                        </td>
                        <td style={{ padding:'8px 12px' }}>
                          {foto
                            ? <img src={foto} alt="" style={{ width:60, height:40, objectFit:'cover', borderRadius:6, cursor:'pointer' }}
                                onClick={() => setDetalleItem(item)}
                                onError={e => { e.target.style.display='none'; }} />
                            : <div style={{ width:60, height:40, borderRadius:6, backgroundColor:t.bgPanel, display:'flex', alignItems:'center', justifyContent:'center', color:t.textMuted, fontSize:12 }}>🏠</div>
                          }
                        </td>
                        <td style={{ padding:'8px 12px', maxWidth:180 }}>
                          <button onClick={() => setDetalleItem(item)} style={{ background:'none', border:'none', cursor:'pointer', textAlign:'left', fontWeight:600, color:t.textPrimary, fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block', maxWidth:'100%' }}>
                            {item.direccion || '—'}
                          </button>
                          <span style={{ fontSize:10, color:t.textMuted }}>{item.municipio}</span>
                        </td>
                        <td style={{ padding:'8px 12px', color:t.textMuted }}>{traducirTipo(car.tipo_propiedad)}</td>
                        <td style={{ padding:'8px 12px', color:t.textMuted, whiteSpace:'nowrap' }}>
                          {[car.habitaciones != null ? `${car.habitaciones}h` : null, car.banos != null ? `${car.banos}b` : null, car.superficie_m2 ? `${car.superficie_m2}m²` : null].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td style={{ padding:'8px 12px', color:t.textPrimary }}>{item.nombre_contacto || '—'}</td>
                        <td style={{ padding:'8px 12px' }}>
                          {item.telefonos?.[0]
                            ? <a href={`tel:${item.telefonos[0]}`} style={{ fontFamily:'monospace', fontSize:11, color:t.accentBlue, textDecoration:'none' }}>{item.telefonos[0]}</a>
                            : <span style={{ color:t.textMuted }}>—</span>
                          }
                        </td>
                        <td style={{ padding:'8px 12px', color:t.textMuted, textTransform:'capitalize', fontSize:11 }}>{item.portal}</td>
                        <td style={{ padding:'8px 12px' }}><Badge tipo={item.tipo_anunciante} agencia={item.alias_agencia} item={item} t={t} /></td>
                        <td style={{ padding:'8px 12px', color:t.textMuted, fontSize:10, whiteSpace:'nowrap', lineHeight:1.25 }}
                          title="CRM = entrada al scraping; Port. = fecha en el portal">
                          <div>CRM {fmtFecha(item.created_at)}</div>
                          {item.fecha_publicacion && (
                            <div style={{ opacity:0.85 }}>Port. {fmtFecha(item.fecha_publicacion)}</div>
                          )}
                        </td>
                        <td style={{ padding:'8px 12px' }}>
                          <div style={{ display:'flex', gap:4 }}>
                            <button onClick={() => setDetalleItem(item)} title="Ver"
                              style={{ padding:'3px 6px', backgroundColor:t.accentBlueLight, color:t.accentBlue, border:'none', borderRadius:5, cursor:'pointer', fontSize:11 }}>👁</button>
                            {item.telefonos?.[0] && (
                              <button onClick={() => setModalWaItem(item)} title="WhatsApp"
                                style={{ padding:'3px 6px', backgroundColor:'rgba(34,197,94,0.12)', color:t.accentGreen, border:'none', borderRadius:5, cursor:'pointer', fontSize:11 }}>💬</button>
                            )}
                            {item.emails?.[0] && (
                              <button onClick={() => setModalEmailItem(item)} title="Email"
                                style={{ padding:'3px 6px', backgroundColor:t.accentBlueLight, color:t.accentBlue, border:'none', borderRadius:5, cursor:'pointer', fontSize:11 }}>✉️</button>
                            )}
                            <button onClick={() => descartar(item.id)} title="Descartar"
                              style={{ padding:'3px 6px', backgroundColor:'rgba(239,68,68,0.10)', color:t.accentRed, border:'none', borderRadius:5, cursor:'pointer', fontSize:11 }}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modales */}
      {detalleItem && <ModalDetalle item={detalleItem} onClose={() => setDetalleItem(null)} onDescartar={descartar} t={t} />}

      {modalWaItem && (
        <ModalWhatsApp
          lead={{ id:modalWaItem.id, nombre:(modalWaItem.nombre_contacto||'').split(' ')[0], apellido:(modalWaItem.nombre_contacto||'').split(' ').slice(1).join(' '), telefono:modalWaItem.telefonos?.[0]||'' }}
          onClose={() => setModalWaItem(null)}
        />
      )}
      {modalEmailItem && (
        <ModalEmail
          lead={{ id:modalEmailItem.id, nombre:(modalEmailItem.nombre_contacto||'').split(' ')[0], apellido:(modalEmailItem.nombre_contacto||'').split(' ').slice(1).join(' '), email:modalEmailItem.emails?.[0]||'' }}
          onClose={() => setModalEmailItem(null)}
        />
      )}

      {/* ── Botón Cargar más ──────────────────────────────────────────────── */}
      {resultados.length < totalResultados && (
        <div style={{ padding:'20px', textAlign:'center' }}>
          <button
            onClick={cargarMas}
            disabled={cargandoMas}
            style={{
              padding:'10px 28px', borderRadius:8, border:`1px solid ${t.border}`,
              backgroundColor: t.bgCard, color: t.textPrimary, fontSize:13, fontWeight:600,
              cursor: cargandoMas ? 'not-allowed' : 'pointer', opacity: cargandoMas ? 0.6 : 1,
            }}
          >
            {cargandoMas
              ? '⏳ Cargando…'
              : `Cargar más · mostrando ${resultados.length} de ${totalResultados}`}
          </button>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
